import type { ReadonlyNonEmptyArray } from '../../utils'

import type { ConnectwareError, Topic, TopicMessageEvent, TopicsChunk, TopicsNextChunkMetadata, TopicSubscriptionResponse } from '../../domain'

export interface TopicsSubscription {
    /**
     * Listen to messages as batches
     *
     * Will only trigger if there are new inbound messages
     */
    onBatch(listener: (batch: ReadonlyNonEmptyArray<TopicMessageEvent>) => void): VoidFunction

    /**
     * Listen to the last received message
     * @param message
     */
    onLastMessage(listener: (message: TopicMessageEvent) => void): VoidFunction

    /**
     * Subscription is dropped after any errors
     */
    onError(listener: (error: ConnectwareError) => void): VoidFunction

    /**
     * Updates the subscribed topics
     * @param topics
     * @throws never
     */
    subscribe(topics: readonly string[]): Promise<TopicSubscriptionResponse>

    /**
     * End the subscription
     */
    end(): void
}

export interface TopicsService {
    createSubscription(): TopicsSubscription

    createTopic(path: string | null): Topic

    /**
     * This function yields part of the available topics
     *
     * **Only** a single level should be yielded at a time
     * Yielded topics should already be sorted
     *
     * @throws {ConnectwareError} if an error occurs
     */
    fetchTopicsChunk(
        /**
         * If null, the topics yielded will be from the root level
         * Causes error to be thrown if the prefix does not match any topic
         */
        prefix: Topic['fullPath'],

        /**
         * 0 based integer
         *
         * Topics are yielded in chunks, this allows traversal through the them
         */
        reference: TopicsNextChunkMetadata['reference'] | null
    ): Promise<TopicsChunk>
}
